import React, { forwardRef } from 'react';
import {
  Box,
  HeadingText,
  GDSCustomizableComponent,
  GDSHeadingLevel,
  Icon,
  GDSIconSource,
  GDSHeadingSize,
  focusOutlineOuter,
} from '@leagueplatform/genesis-core';
import { uniqueId } from 'lodash-es';

export type AccordionProps = {
  collapseIcon?: GDSIconSource;
  defaultExpanded?: boolean;
  expandIcon?: GDSIconSource;
  heading: string;
  headingLevel?: GDSHeadingLevel;
  headingSize?: GDSHeadingSize;
  isExpanded?: boolean;
  dividerPosition?: 'top' | 'bottom' | 'both' | 'none';
  iconPosition?: 'start' | 'end';
  onClick?: (expandedState: boolean) => void;
  id?: string;
} & GDSCustomizableComponent;

export type GDSAccordionProps = React.PropsWithChildren<AccordionProps>;

const DISPLAY_NAME = 'Accordion';
const CSS_CLASS_NAME = 'GDS-accordion';

export const Accordion = forwardRef<HTMLDivElement, GDSAccordionProps>(
  (
    {
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      children,
      className,
      collapseIcon = 'interfaceChevronDown',
      css,
      defaultExpanded = false,
      dividerPosition = 'bottom',
      iconPosition = 'end',
      expandIcon = 'interfaceChevronUp',
      heading,
      headingLevel = '3',
      headingSize = 'lg',
      isExpanded: isExpandedProp,
      onClick: onClickCallback,
      id,
    },
    ref,
  ) => {
    const isControlled = isExpandedProp !== undefined;
    const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
    const isExpandedState = React.useMemo(
      () => (isControlled ? isExpandedProp : isExpanded),
      [isControlled, isExpanded, isExpandedProp],
    );
    const composedClassName = React.useMemo(
      () => [CSS_CLASS_NAME, className].join(' '),
      [className],
    );
    const headingId = React.useMemo(
      () => id ?? uniqueId('GDS-accordion-heading-'),
      [id],
    );
    const contentId = React.useMemo(
      () => uniqueId('GDS-accordion-content-'),
      [],
    );

    if (isControlled && onClickCallback === undefined) {
      console.warn(
        'Accordion is being used in controlled mode without an onClick handler. This will result in a read-only accordion.',
      );
    }

    const handleClick = React.useCallback(() => {
      if (!isControlled) {
        setIsExpanded(!isExpandedState);
      }
      if (onClickCallback) {
        onClickCallback(isExpandedState);
      }
    }, [isControlled, isExpandedState, onClickCallback]);

    const hasDividerTop =
      dividerPosition === 'top' || dividerPosition === 'both';
    const hasDividerBottom =
      dividerPosition === 'bottom' || dividerPosition === 'both';

    return (
      <Box
        css={{
          borderBlockColor: '$onSurfaceBorderDefault',
          borderBlockWidth: '$thin',
          borderBlockStartStyle: hasDividerTop ? 'solid' : 'none',
          borderBlockEndStyle: hasDividerBottom ? 'solid' : 'none',
          ...css,
        }}
        className={composedClassName}
        ref={ref}
        data-testid={testId}
      >
        <HeadingText
          className={`${CSS_CLASS_NAME}-heading`}
          size={headingSize}
          level={headingLevel}
          id={headingId}
        >
          <Box
            as="button"
            className={`${CSS_CLASS_NAME}-button`}
            aria-expanded={isExpandedState}
            aria-controls={contentId}
            css={{
              all: 'inherit',
              display: 'flex',
              gap: '$quarter',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              paddingBlock: '$oneAndHalf',
              width: '100%',
              '&:hover': {
                backgroundColor: '$interactiveBackgroundHovered',
                cursor: 'pointer',
              },
              '&[class*="GDS"]:focus': {
                outline: 'none',
              },
              '&[class*="GDS"]:focus-visible': {
                ...focusOutlineOuter,
              },
            }}
            onClick={handleClick}
          >
            {iconPosition === 'start' && (
              <Icon
                className={`${CSS_CLASS_NAME}-button-icon`}
                aria-hidden
                icon={isExpandedState ? expandIcon : collapseIcon}
              />
            )}
            <Box
              as="span"
              css={{ flex: 1 }}
              className={`${CSS_CLASS_NAME}-button-label`}
            >
              {heading}
            </Box>
            {iconPosition === 'end' && (
              <Icon
                className={`${CSS_CLASS_NAME}-button-icon`}
                aria-hidden
                css={{ flexShrink: 0 }}
                icon={isExpandedState ? expandIcon : collapseIcon}
              />
            )}
          </Box>
        </HeadingText>
        <Box
          id={contentId}
          aria-labelledby={headingId}
          className={`${CSS_CLASS_NAME}-content`}
          css={{
            paddingBlockEnd: '$oneAndHalf',
          }}
          {...(isExpandedState ? {} : { hidden: true })}
        >
          {children}
        </Box>
      </Box>
    );
  },
);

Accordion.displayName = DISPLAY_NAME;
